import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, FormInput, Textarea, Card, Progress, Badge } from '../shared';

const STEPS = [
  { id: 1, name: 'Account', description: 'Create your account' },
  { id: 2, name: 'Profile', description: 'Tell us about yourself' },
  { id: 3, name: 'Education', description: 'Academic background' },
  { id: 4, name: 'Interests', description: 'Career preferences' },
  { id: 5, name: 'Review', description: 'Review and submit' },
];

const Signup = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Account
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Step 2: Profile
    age: '',
    phone: '',
    city: '',
    state: '',
    // Step 3: Education
    school: '',
    degreeProgram: '',
    graduationYear: '',
    // Step 4: Career Interests
    employmentType: 'internship',
    desiredLocation: '',
    availability: [],
    familyInBusiness: false,
    whyInterested: '',
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!formData.email.endsWith('.edu')) {
        newErrors.email = 'Must be a valid .edu email address';
      }
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else if (step === 2) {
      if (!formData.age) {
        newErrors.age = 'Age is required';
      } else if (formData.age < 16 || formData.age > 100) {
        newErrors.age = 'Please enter a valid age';
      }
      if (!formData.phone?.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.city?.trim()) newErrors.city = 'City is required';
      if (!formData.state?.trim()) newErrors.state = 'State is required';
    } else if (step === 3) {
      if (!formData.school?.trim()) newErrors.school = 'School name is required';
      if (!formData.degreeProgram?.trim())
        newErrors.degreeProgram = 'Degree program is required';
      if (!formData.graduationYear) {
        newErrors.graduationYear = 'Graduation year is required';
      } else if (
        formData.graduationYear < new Date().getFullYear() ||
        formData.graduationYear > new Date().getFullYear() + 10
      ) {
        newErrors.graduationYear = 'Please enter a valid graduation year';
      }
    } else if (step === 4) {
      if (!formData.desiredLocation?.trim())
        newErrors.desiredLocation = 'Desired location is required';
      if (!formData.whyInterested?.trim()) {
        newErrors.whyInterested = 'Please tell us why you are interested';
      } else if (formData.whyInterested.length < 50) {
        newErrors.whyInterested = 'Please provide at least 50 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < STEPS.length) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      } else {
        // Final submission
        if (onComplete) {
          onComplete(formData);
        }
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <FormInput
        label="First Name"
        value={formData.firstName}
        onChange={(e) => handleInputChange('firstName', e.target.value)}
        error={errors.firstName}
        required
        placeholder="John"
      />
      <FormInput
        label="Last Name"
        value={formData.lastName}
        onChange={(e) => handleInputChange('lastName', e.target.value)}
        error={errors.lastName}
        required
        placeholder="Doe"
      />
      <FormInput
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        error={errors.email}
        helperText="Must be a valid .edu email address"
        required
        placeholder="john.doe@university.edu"
      />
      <FormInput
        label="Password"
        type="password"
        value={formData.password}
        onChange={(e) => handleInputChange('password', e.target.value)}
        error={errors.password}
        helperText="At least 8 characters"
        required
      />
      <FormInput
        label="Confirm Password"
        type="password"
        value={formData.confirmPassword}
        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
        error={errors.confirmPassword}
        required
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <FormInput
        label="Age"
        type="number"
        value={formData.age}
        onChange={(e) => handleInputChange('age', parseInt(e.target.value))}
        error={errors.age}
        required
        min="16"
        max="100"
      />
      <FormInput
        label="Phone Number"
        type="tel"
        value={formData.phone}
        onChange={(e) => handleInputChange('phone', e.target.value)}
        error={errors.phone}
        required
        placeholder="(555) 123-4567"
      />
      <FormInput
        label="City"
        value={formData.city}
        onChange={(e) => handleInputChange('city', e.target.value)}
        error={errors.city}
        required
        placeholder="San Francisco"
      />
      <FormInput
        label="State"
        value={formData.state}
        onChange={(e) => handleInputChange('state', e.target.value)}
        error={errors.state}
        required
        maxLength="2"
        placeholder="CA"
        helperText="Two-letter state code"
      />
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <FormInput
        label="School"
        value={formData.school}
        onChange={(e) => handleInputChange('school', e.target.value)}
        error={errors.school}
        required
        placeholder="Stanford University"
      />
      <FormInput
        label="Degree Program"
        value={formData.degreeProgram}
        onChange={(e) => handleInputChange('degreeProgram', e.target.value)}
        error={errors.degreeProgram}
        required
        placeholder="Finance"
      />
      <FormInput
        label="Expected Graduation Year"
        type="number"
        value={formData.graduationYear}
        onChange={(e) => handleInputChange('graduationYear', parseInt(e.target.value))}
        error={errors.graduationYear}
        required
        min={new Date().getFullYear()}
        max={new Date().getFullYear() + 10}
      />
    </div>
  );

  const handleToggleAvailability = (season) => {
    const newAvailability = formData.availability.includes(season)
      ? formData.availability.filter((s) => s !== season)
      : [...formData.availability, season];
    handleInputChange('availability', newAvailability);
  };

  const renderStep4 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Employment Type <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {['internship', 'full-time'].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleInputChange('employmentType', type)}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                formData.employmentType === type
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium capitalize">{type.replace('-', ' ')}</span>
                {formData.employmentType === type && (
                  <svg
                    className="h-5 w-5 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      <FormInput
        label="Desired Location"
        value={formData.desiredLocation}
        onChange={(e) => handleInputChange('desiredLocation', e.target.value)}
        error={errors.desiredLocation}
        required
        placeholder="San Francisco, CA"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Availability
        </label>
        <div className="flex flex-wrap gap-2">
          {['Spring', 'Summer', 'Fall', 'Winter'].map((season) => (
            <button
              key={season}
              type="button"
              onClick={() => handleToggleAvailability(season.toLowerCase())}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                formData.availability.includes(season.toLowerCase())
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              {season}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="familyInBusiness"
          checked={formData.familyInBusiness}
          onChange={(e) => handleInputChange('familyInBusiness', e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="familyInBusiness" className="ml-2 text-sm text-gray-700">
          I have family in the financial services business
        </label>
      </div>

      <Textarea
        label="Why are you interested in financial services?"
        value={formData.whyInterested}
        onChange={(e) => handleInputChange('whyInterested', e.target.value)}
        error={errors.whyInterested}
        required
        rows={4}
        maxLength={500}
        showCount
        helperText="Tell us about your interest in financial services (minimum 50 characters)"
      />
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Review Your Information</h3>
        <p className="text-sm text-gray-600">
          Please review your information before submitting
        </p>
      </div>

      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-gray-900">Account Information</h4>
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-600">Name:</dt>
              <dd className="text-gray-900 font-medium">
                {formData.firstName} {formData.lastName}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Email:</dt>
              <dd className="text-gray-900">{formData.email}</dd>
            </div>
          </dl>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-gray-900">Profile Information</h4>
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-600">Age:</dt>
              <dd className="text-gray-900">{formData.age}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Phone:</dt>
              <dd className="text-gray-900">{formData.phone}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Location:</dt>
              <dd className="text-gray-900">
                {formData.city}, {formData.state}
              </dd>
            </div>
          </dl>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-gray-900">Education</h4>
            <button
              type="button"
              onClick={() => setCurrentStep(3)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-600">School:</dt>
              <dd className="text-gray-900">{formData.school}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Degree:</dt>
              <dd className="text-gray-900">{formData.degreeProgram}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Graduation:</dt>
              <dd className="text-gray-900">{formData.graduationYear}</dd>
            </div>
          </dl>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-gray-900">Career Interests</h4>
            <button
              type="button"
              onClick={() => setCurrentStep(4)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-600">Employment Type:</dt>
              <dd className="text-gray-900 capitalize">{formData.employmentType}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Desired Location:</dt>
              <dd className="text-gray-900">{formData.desiredLocation}</dd>
            </div>
            {formData.availability.length > 0 && (
              <div className="flex justify-between">
                <dt className="text-gray-600">Availability:</dt>
                <dd className="text-gray-900 capitalize">
                  {formData.availability.join(', ')}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return null;
    }
  };

  const progress = (currentStep / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Create Your Student Profile
          </h1>
          <p className="text-gray-600">
            Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].description}
          </p>
        </div>

        {/* Progress Bar */}
        <Progress value={progress} className="mb-8" />

        {/* Progress Dots */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-2">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                    step.id < currentStep
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : step.id === currentStep
                        ? 'bg-white border-blue-600 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-400'
                  }`}
                >
                  {step.id < currentStep ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <span className="text-sm font-semibold">{step.id}</span>
                  )}
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`w-12 h-1 ${
                      step.id < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <Card padding="lg" className="mb-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            {renderCurrentStep()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="ghost"
                onClick={handleBack}
                disabled={currentStep === 1}
              >
                Back
              </Button>
              <Button type="submit" variant="primary">
                {currentStep === STEPS.length ? 'Submit' : 'Next'}
              </Button>
            </div>
          </form>
        </Card>

        {/* Login Link */}
        <p className="text-center text-sm text-gray-600">
          Already have an account?{' '}
          <a href="/student/login" className="text-blue-600 hover:text-blue-700 font-medium">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
};

Signup.propTypes = {
  onComplete: PropTypes.func,
};

export default Signup;

